import type { Components } from "@mui/material";
import {
  BACKGROUND_COLOR,
  BLACK,
  DARK_CHARCOAL,
  DARK_GREY,
  DARK_GREYISH,
  PRIMARY_COLOR,
  RED,
  SECONDARY_DARK,
  WHITE_COLOR,
} from "../palette";

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    sidebarTitle: true;
    sidebarTreeTitle: true;
    header: true;
    text: true;
    subHeader: true;
    highlightedText: true;
    title: true;
    price: true;
    listPrice: true;
    discountedPrice: true;
    body3: true;
    body4: true;
    heading3_regular: true;
    analytics: true;
    analyticsList: true;
    badges: true;
    productGroupComponentsTitle: true;
    linkedCategoriesText: true;
    h6: true;
    inventory: true;
    coverage: true;
    position: true;
    syncBarHeader: true;
    title2: true;
    gridTitle: true;
    tabsHeader: true;
    heading: true;
    modalSubtitle: true;
    scaleNumbers: true;
  }
}

export const MuiTypography: Components["MuiTypography"] = {
  variants: [
    {
      props: { variant: "sidebarTitle" } /* component props */,
      style: {
        color: "#404041",
        display: "block",
      },
    },
    {
      props: { variant: "sidebarTreeTitle" },
      style: {
        fontSize: "14px",
        "&:hover": {
          color: "#78a6be",
        },
      },
    },
    {
      props: { variant: "header" },
      style: {
        fontSize: "1.25em",
        fontWeight: "bold",
        color: "#404041",
        textAlign: "center",
        paddingBottom: "6px",
      },
    },
    {
      props: { variant: "subHeader" },
      style: {
        fontSize: "14px",
        fontWeight: 700,
        fontFamily: "Helvetica",
        color: DARK_GREYISH,
        textAlign: "center",
        paddingBottom: "6px",
      },
    },
    {
      props: { variant: "text" },
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        color: "#709bb1",
      },
    },
    {
      props: { variant: "highlightedText" },
      style: {
        fontSize: "14px",
        color: "#f18308",
      },
    },
    {
      props: { variant: "subtitle1" },
      style: {
        fontSize: "18px",
        color: BLACK,
        fontWeight: 400,
        fontFamily: "Helvetica",
      },
    },
    {
      props: { variant: "modalSubtitle" },
      style: {
        fontSize: "18px",
        color: BLACK,
        fontWeight: 700,
        fontFamily: "Helvetica",
      },
    },
    {
      props: { variant: "subtitle2" },
      style: {
        fontSize: "14px",
        color: DARK_CHARCOAL,
        fontWeight: 400,
        fontFamily: "Helvetica",
      },
    },
    {
      props: { variant: "title" },
      style: {
        fontSize: 14,
        color: BLACK,
        fontWeight: 700,
        fontFamily: "Helvetica",
      },
    },
    {
      props: { variant: "price" },
      style: {
        fontSize: 14,
        color: BLACK,
        fontWeight: 400,
        fontFamily: "Helvetica",
      },
    },
    {
      props: { variant: "listPrice" },
      style: {
        fontSize: 14,
        color: BLACK,
        fontWeight: 400,
        fontFamily: "Helvetica",
        textDecoration: "line-through",
      },
    },
    {
      props: { variant: "discountedPrice" },
      style: {
        fontSize: 14,
        color: RED,
        fontWeight: 700,
        fontFamily: "Helvetica",
      },
    },
    {
      props: { variant: "body3" },
      style: {
        fontSize: 12,
        color: SECONDARY_DARK,
        fontWeight: 400,
        fontFamily: "Helvetica",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: "85%",
        display: "inline-block",
      },
    },
    {
      props: { variant: "heading3_regular" },
      style: {
        fontSize: 16,
        color: DARK_GREY,
        fontWeight: 400,
        fontFamily: "Helvetica",
      },
    },
    {
      props: { variant: "analytics" },
      style: {
        fontSize: 14,
        color: PRIMARY_COLOR,
        fontWeight: 700,
        fontFamily: "Helvetica",
      },
    },
    {
      props: { variant: "analyticsList" },
      style: {
        fontSize: 12,
        fontWeight: 700,
        fontFamily: "Helvetica",
        color: BACKGROUND_COLOR,
      },
    },
    {
      props: { variant: "badges" },
      style: {
        fontSize: "14px !important",
        color: BACKGROUND_COLOR,
        fontWeight: 700,
        fontFamily: "Helvetica",
      },
    },
    {
      props: { variant: "productGroupComponentsTitle" },
      style: {
        fontSize: "16px",
        color: PRIMARY_COLOR,
        fontWeight: 700,
        fontFamily: "Helvetica",
      },
    },
    {
      props: { variant: "linkedCategoriesText" },
      style: {
        fontSize: "14px",
        fontWeight: 400,
        fontFamily: "Helvetica",
        color: BLACK,
        textAlign: "center",
      },
    },
    {
      props: { variant: "h6" },
      style: {
        fontSize: "24px",
        fontWeight: 700,
        fontFamily: "Helvetica",
        color: BLACK,
        textAlign: "center",
      },
    },
    {
      props: { variant: "inventory" },
      style: {
        fontSize: "14px",
        fontWeight: 700,
        fontFamily: "Helvetica",
        color: DARK_GREYISH,
        textAlign: "center",
        paddingBottom: "6px",
      },
    },
    {
      props: { variant: "coverage" },
      style: {
        fontSize: "12px",
        fontWeight: 400,
        fontFamily: "Helvetica",
        color: DARK_CHARCOAL,
        textAlign: "center",
        paddingBottom: "6px",
      },
    },
    {
      props: { variant: "position" },
      style: {
        fontSize: "0.8rem",
        fontWeight: 400,
        fontFamily: "Helvetica",
        color: DARK_CHARCOAL,
        textAlign: "center",
        paddingBottom: "6px",
      },
    },
    {
      props: { variant: "syncBarHeader" },
      style: {
        fontSize: "14px",
        fontWeight: 700,
        fontFamily: "Helvetica",
        color: WHITE_COLOR,
      },
    },
    {
      props: { variant: "title2" },
      style: {
        fontSize: "12px",
        fontWeight: 400,
        fontFamily: "Helvetica",
        color: BLACK,
        lineHeight: 1.43,
        display: "block",
      },
    },
    {
      props: { variant: "gridTitle" },
      style: {
        fontSize: 12,
        color: BLACK,
        fontWeight: 700,
        fontFamily: "Helvetica",
        lineHeight: 1.43,
        display: "block",
      },
    },
    {
      props: { variant: "tabsHeader" },
      style: {
        fontSize: "14px",
        fontWeight: 700,
        fontFamily: "Helvetica",
        color: DARK_GREYISH,
        textAlign: "center",
      },
    },
    {
      props: { variant: "heading" },
      style: {
        fontSize: "16px",
        color: BLACK,
        fontWeight: 700,
        fontFamily: "Helvetica",
      },
    },
    {
      props: { variant: "scaleNumbers" },
      style: {
        fontSize: "12px",
        color: DARK_CHARCOAL,
        fontWeight: 400,
        fontFamily: "Helvetica",
      },
    }
  ],
};
